import * as i0 from '@angular/core';
import { Directive, Inject, Input, HostBinding, HostListener, EventEmitter, Output, NgModule } from '@angular/core';
import * as i1 from '@tn-equis/core/services';
import { EquisWindowService } from '@tn-equis/core/services';
import { UntypedFormControl, FormsModule } from '@angular/forms';
import { DOCUMENT, CommonModule } from '@angular/common';
import { EquisCurrencyTypes } from '@tn-equis/core/utils';

class EquisBrowserDirective {
    constructor(renderer, element, windowService) {
        this.renderer = renderer;
        this.element = element;
        this.windowService = windowService;
        this.renderer.addClass(element.nativeElement, `browser-${this.windowService.getBrowser()}`);
    }
}
EquisBrowserDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisBrowserDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: EquisWindowService }], target: i0.ɵɵFactoryTarget.Directive });
EquisBrowserDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: EquisBrowserDirective, selector: "[zBrowser], [EquisBrowser]", exportAs: ["zBrowser", "EquisBrowser"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisBrowserDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[zBrowser], [EquisBrowser]',
                    exportAs: 'zBrowser, EquisBrowser',
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i1.EquisWindowService, decorators: [{
                    type: Inject,
                    args: [EquisWindowService]
                }] }]; } });

class EquisErrorFeedbackDirective {
    constructor(el) {
        this.el = el;
        this.control = new UntypedFormControl();
        this.controlWithError = false;
        this.controlWithSuccess = false;
    }
    ngOnInit() {
        this.statusChangesSubscription = this.control.statusChanges.subscribe(() => {
            this.controlWithError = this.control.invalid && this.control.dirty;
        });
    }
}
EquisErrorFeedbackDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisErrorFeedbackDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
EquisErrorFeedbackDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: EquisErrorFeedbackDirective, selector: "[zErrorFeedBack], [equisErrorFeedBack]", inputs: { control: "control" }, host: { properties: { "class.has-danger": "this.controlWithError", "class.has-success": "this.controlWithSuccess" } }, exportAs: ["zErrorFeedBack", "equisErrorFeedBack"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisErrorFeedbackDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[zErrorFeedBack], [equisErrorFeedBack]',
                    exportAs: 'zErrorFeedBack, equisErrorFeedBack',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { control: [{
                type: Input
            }], controlWithError: [{
                type: HostBinding,
                args: ['class.has-danger']
            }], controlWithSuccess: [{
                type: HostBinding,
                args: ['class.has-success']
            }] } });

class EquisHoverDirective {
    constructor(el, renderer, documentService, navigatorService) {
        this.el = el;
        this.renderer = renderer;
        this.documentService = documentService;
        this.navigatorService = navigatorService;
        this.isHover = false;
        this.stylesEnter = [];
        this.stylesLeave = [];
        this.breakpointMinWithoutStyles = 0;
        this.style = {};
        this.calculateWidthViewport();
    }
    calculateWidthViewport() {
        this.widthViewport = this.documentService.nativeDocument.body.offsetWidth;
    }
    ngOnInit() {
        if (this.navigatorService.isBrowser) {
            this.style = getComputedStyle(this.el.nativeElement);
            this.width = this.westernArabicNumeralsOnly(this.style.width);
        }
    }
    onMouseEnter() {
        if (this.isHover &&
            this.breakpointMinWithoutStyles !== null &&
            this.widthViewport >= this.breakpointMinWithoutStyles) {
            for (const style of this.stylesEnter) {
                this.renderer.setStyle(this.el.nativeElement, style.style, style.value);
            }
        }
    }
    onMouseLeave() {
        if (this.isHover &&
            this.breakpointMinWithoutStyles !== null &&
            this.widthViewport >= this.breakpointMinWithoutStyles) {
            for (const style of this.stylesLeave) {
                this.renderer.setStyle(this.el.nativeElement, style.style, style.value);
            }
        }
    }
    westernArabicNumeralsOnly(style) {
        return style.match(/\d+/g).map(Number)[0];
    }
}
EquisHoverDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisHoverDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1.EquisDocumentService }, { token: i1.EquisNavigatorService }], target: i0.ɵɵFactoryTarget.Directive });
EquisHoverDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: EquisHoverDirective, selector: "[zHover], [equisHover]", inputs: { isHover: "isHover", stylesEnter: "stylesEnter", stylesLeave: "stylesLeave", breakpointMinWithoutStyles: "breakpointMinWithoutStyles" }, host: { listeners: { "mouseenter": "onMouseEnter()", "mouseleave": "onMouseLeave()" } }, exportAs: ["zHover", "equisHover"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisHoverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[zHover], [equisHover]',
                    exportAs: 'zHover, equisHover',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1.EquisDocumentService }, { type: i1.EquisNavigatorService }]; }, propDecorators: { isHover: [{
                type: Input
            }], stylesEnter: [{
                type: Input
            }], stylesLeave: [{
                type: Input
            }], breakpointMinWithoutStyles: [{
                type: Input
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }], onMouseLeave: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

class EquisOuterClickDirective {
    constructor(_elementRef) {
        this._elementRef = _elementRef;
        this.clickOutside = new EventEmitter();
        this.disabledClose = false;
    }
    // Attaching document click.
    onClick(targetElement) {
        if (!this.disabledClose) {
            const clickedInside = this._elementRef.nativeElement.contains(targetElement);
            if (!clickedInside) {
                // Just emit event to notify the outside click
                this.clickOutside.emit(targetElement);
            }
        }
    }
}
EquisOuterClickDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisOuterClickDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
EquisOuterClickDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: EquisOuterClickDirective, selector: "[zOuterClick], [EquisOuterClick]", inputs: { disabledClose: "disabledClose" }, outputs: { clickOutside: "clickOutside" }, host: { listeners: { "document:click": "onClick($event.target)" } }, exportAs: ["zOuterClick", "EquisOuterClick"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisOuterClickDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[zOuterClick], [EquisOuterClick]',
                    exportAs: 'zOuterClick, EquisOuterClick',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { clickOutside: [{
                type: Output
            }], disabledClose: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }] } });

class EquisThemeDirective {
    constructor(document, renderer) {
        this.document = document;
        this.renderer = renderer;
    }
    set theme(theme) {
        this.removeThemeClass();
        this.addThemeClass(theme);
    }
    removeThemeClass() {
        if (this.currentTheme) {
            this.renderer.removeClass(this.document.body, this.currentTheme);
        }
    }
    addThemeClass(theme) {
        this.currentTheme = theme;
        this.renderer.addClass(this.document.body, this.currentTheme);
    }
}
EquisThemeDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisThemeDirective, deps: [{ token: DOCUMENT }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
EquisThemeDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: EquisThemeDirective, selector: "[zTheme], [equisTheme]", inputs: { theme: "theme" }, exportAs: ["zTheme", "equisTheme"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisThemeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[zTheme], [equisTheme]',
                    exportAs: 'zTheme, equisTheme',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.Renderer2 }]; }, propDecorators: { theme: [{
                type: Input
            }] } });

class EquisCurrencyMaskDirective {
    constructor(elRef, currencyMaskService) {
        this.elRef = elRef;
        this.currencyMaskService = currencyMaskService;
        this.currency = EquisCurrencyTypes.ARS;
    }
    ngOnChanges(changes) {
        if (changes.number && this.number) {
            this.currencyMaskService.setMask(this.elRef.nativeElement, this.currency);
        }
    }
}
EquisCurrencyMaskDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisCurrencyMaskDirective, deps: [{ token: i0.ElementRef }, { token: i1.EquisCurrencyMaskService }], target: i0.ɵɵFactoryTarget.Directive });
EquisCurrencyMaskDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: EquisCurrencyMaskDirective, selector: "[zCurrencyMask], [equisCurrencyMask]", inputs: { number: "number", currency: "currency" }, exportAs: ["zurrencyMask", "equisurrencyMask"], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisCurrencyMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[zCurrencyMask], [equisCurrencyMask]',
                    exportAs: 'zurrencyMask, equisurrencyMask',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1.EquisCurrencyMaskService }]; }, propDecorators: { number: [{
                type: Input
            }], currency: [{
                type: Input
            }] } });

const commonPipes = [
    EquisBrowserDirective,
    EquisErrorFeedbackDirective,
    EquisCurrencyMaskDirective,
    EquisHoverDirective,
    EquisOuterClickDirective,
    EquisThemeDirective,
];
class EquisDirectiveModule {
    constructor() { }
    static forRoot() {
        return {
            ngModule: EquisDirectiveModule,
        };
    }
}
EquisDirectiveModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisDirectiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
EquisDirectiveModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: EquisDirectiveModule, declarations: [EquisBrowserDirective,
        EquisErrorFeedbackDirective,
        EquisCurrencyMaskDirective,
        EquisHoverDirective,
        EquisOuterClickDirective,
        EquisThemeDirective], imports: [CommonModule, FormsModule], exports: [EquisBrowserDirective,
        EquisErrorFeedbackDirective,
        EquisCurrencyMaskDirective,
        EquisHoverDirective,
        EquisOuterClickDirective,
        EquisThemeDirective] });
EquisDirectiveModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisDirectiveModule, imports: [CommonModule, FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisDirectiveModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule],
                    declarations: commonPipes,
                    exports: commonPipes,
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Generated bundle index. Do not edit.
 */

export { EquisBrowserDirective, EquisCurrencyMaskDirective, EquisDirectiveModule, EquisErrorFeedbackDirective, EquisHoverDirective, EquisOuterClickDirective, EquisThemeDirective, commonPipes };
